import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatPlatform } from "../lib/utils";
import { CheckCircle2, Tag } from "lucide-react";
import Loading from "../components/Loading";

export default function OrderSuccessPage() {
  const { id } = useParams();
  const [o, setO] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/orders/${id}`);
      setO(r.data);
    })();
  }, [id]);

  if (!o) return <Loading variant="page" label="Loading order" />;
  const formatMoney = (value) => value.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="text-center mb-10">
        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-4xl font-black uppercase tracking-tighter">Order <span className="text-gold">Placed!</span></h1>
        <p className="text-slate-300 mt-2 text-lg">Thanks for shopping with us! 🎮</p>
        <p className="text-slate-400 mt-1 text-sm">Your order <span className="text-gold font-bold">#{o.order_number}</span> has been confirmed.</p>
      </div>
      <div className="bg-navy-700 rounded-xl p-6 neon-border space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-gold mb-3">Items</div>
          <div className="space-y-3">
            {o.items.map((it, i) => (
              <Link key={i} to={`/product/${it.product_id}`} className="flex gap-3 items-center hover:bg-navy-800/60 rounded-lg px-2 py-1 -mx-2 transition">
                {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover rounded-lg shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm hover:text-gold transition">{it.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    Qty {it.quantity}
                    {it.platform && <span className="ml-1">· {formatPlatform(it.platform)}</span>}
                    {it.condition && <span className="ml-1">· {it.condition.charAt(0).toUpperCase() + it.condition.slice(1)}</span>}
                  </div>
                </div>
                <div className="font-black text-gold text-sm">₹{(it.price * it.quantity).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </div>
        <div className="border-t border-gold/20 pt-4 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span>₹{formatMoney(o.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span>{o.shipping === 0 ? "FREE" : `₹${o.shipping}`}</span></div>
          {(o.processing_fee || 0) > 0 && (
            <div className="flex justify-between">
              <span className="text-slate-400">Digital processing (2.5%)</span>
              <span>₹{formatMoney(o.processing_fee)}</span>
            </div>
          )}
          {o.coupon_code && (
            <div className="flex justify-between text-emerald-400">
              <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />{o.coupon_code}</span>
              <span>−₹{formatMoney(o.coupon_discount || 0)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-black mt-2"><span>Total</span><span className="text-gold">₹{formatMoney(o.total)}</span></div>
        </div>
        <div className="text-xs text-slate-400">Shipping to: {o.shipping_address}, {o.city}, {o.state} - {o.pincode}</div>
      </div>
      <div className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to={`/track?q=${o.order_number}`} className="inline-block bg-navy-700 border border-gold/30 text-gold font-extrabold uppercase tracking-wider text-sm px-7 py-3 rounded-full hover:border-gold transition-all">Track Order</Link>
        <Link to="/shop" className="inline-block bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-7 py-3 rounded-full hover:bg-gold-light btn-glow transition-all">Continue Shopping</Link>
      </div>
    </div>
  );
}
